import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { useCallback, useEffect, useMemo, useState } from "react";
import { usePermissions } from "./usePermissions";

interface MenuItem {
  id: number;
  nome?: string;
  rota?: string;
  visivel?: boolean;
  ordem?: number;
}

/**
 * Mapeia prefixos de path para a rota do menu (para rotas dinâmicas).
 * Ex: /atendimento/123 -> permissão verificada pelo menu com rota /atendimentos
 */
const PATH_PREFIX_TO_MENU_ROTA: Record<string, string> = {
  "/atendimento": "/atendimentos",
};

/** Paths que devem ser considerados equivalentes ao menu rota (ex.: aliases de URL) */
const PATH_ALIAS_TO_MENU_ROTA: Record<string, string> = {
  "/validar-documento": "/assinaturas/validar-documento",
  "/validar-documentos": "/assinaturas/validar-documento",
  "/assinaturas/validar-documentos": "/assinaturas/validar-documento",
};

export function useRouteAccess() {
  const { user } = useAuth();
  const { hasMenuAccess, isAdmin, loading: permissionsLoading } = usePermissions();
  const [menus, setMenus] = useState<MenuItem[]>([]);
  const [menusLoading, setMenusLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetchMenus = async () => {
      try {
        setMenusLoading(true);
        const response = await api.get("/api/menu_sistema");
        const list = (response?.result || []) as MenuItem[];
        if (!cancelled) setMenus(Array.isArray(list) ? list : []);
      } catch (e) {
        if (!cancelled) setMenus([]);
      } finally {
        if (!cancelled) setMenusLoading(false);
      }
    };
    fetchMenus();
    return () => { cancelled = true; };
  }, []);

  const allowedRotas = useMemo(() => {
    if (isAdmin) return null; // null = todas permitidas
    return menus
      .filter((m) => m.visivel !== false && m.rota)
      .filter((m) => hasMenuAccess(m.id))
      .map((m) => (m.rota || "").trim().toLowerCase())
      .filter(Boolean);
  }, [menus, hasMenuAccess, isAdmin]);

  /** Primeiro menu liberado para o usuário (por ordem), para usar como redirecionamento padrão */
  const firstAllowedRoute = useMemo(() => {
    if (isAdmin) return "/dashboard";
    const allowed = menus
      .filter((m) => m.visivel !== false && (m.rota || "").trim())
      .filter((m) => hasMenuAccess(m.id))
      .sort((a, b) => (a.ordem ?? 0) - (b.ordem ?? 0));
    const first = allowed[0];
    const rota = (first?.rota || "").trim();
    return rota || "/dashboard";
  }, [menus, hasMenuAccess, isAdmin]);

  const canAccessPath = useCallback(
    (pathname: string): boolean => {
      if (!user) return false;
      if (permissionsLoading || menusLoading) return true; // enquanto carrega, deixa passar (evita flash)
      if (isAdmin) return true;

      const path = pathname.replace(/\/$/, "") || "/";
      const pathLower = path.toLowerCase();

      // Rotas públicas (não exigem menu)
      const publicPaths = ["/login", "/configurar-senha", "/google-meet/callback"];
      if (publicPaths.some((p) => pathLower === p || pathLower.startsWith(p + "/")))
        return true;

      if (!allowedRotas || allowedRotas.length === 0) return false;

      // Alias de path -> rota do menu (ex: /validar-documento -> /assinaturas/validar-documento)
      const aliasRota = PATH_ALIAS_TO_MENU_ROTA[pathLower];
      if (aliasRota && allowedRotas.includes(aliasRota.toLowerCase()))
        return true;

      // Verificação por prefixo mapeado (ex: /atendimento/123 -> /atendimentos)
      for (const [prefix, menuRota] of Object.entries(PATH_PREFIX_TO_MENU_ROTA)) {
        if (pathLower.startsWith(prefix + "/") || pathLower === prefix) {
          return allowedRotas.includes(menuRota.toLowerCase());
        }
      }

      // Match exato ou subpath da rota do menu
      if (allowedRotas.includes(pathLower)) return true;
      for (const rota of allowedRotas) {
        if (rota && (pathLower === rota || pathLower.startsWith(rota + "/")))
          return true;
      }
      return false;
    },
    [user, isAdmin, permissionsLoading, menusLoading, allowedRotas]
  );

  return {
    canAccessPath,
    firstAllowedRoute,
    loading: permissionsLoading || menusLoading,
  };
}
